import type { Faction } from '../constants.ts';
import type { Vec3 } from '../math.ts';
import type { Arena } from '../arena.ts';
import { isEmbodied, type PlayerState } from '../player.ts';
import { colOf, isOpen, rowOf, type NavGrid } from './nav.ts';

/**
 * What every square of the arena is worth, and how dangerous it is to stand on.
 *
 * The bots used to reason entirely in distances — nearest pool, nearest enemy,
 * nearest friend — and every failure they had came from that. Distance cannot
 * tell you that three mages are waiting round the corner, or which of two pools
 * your side is winning near, or that the ground between here and there is a
 * killing floor. So it produced bots that walked into crowds because the crowd
 * was close, and bots that would not advance because nothing ever said advance.
 *
 * Three scalar fields over the navigation grid answer all of that:
 *
 *   presence  where each faction's mages are, spread out from them
 *   value     what a square is worth to a faction: pools, shrines, the way in
 *   (threat and support are both read off `presence`, from whose side you are)
 *
 * Everything else is arithmetic on those. Retreating is walking uphill on
 * support minus threat; probing is following the contour where they cancel;
 * attacking is finding the best value the threat allows. Each class differs only
 * in how much threat it will tolerate for a given prize, which is a curve rather
 * than a rung on a ladder — and *that* is what a priority ladder could never
 * express, because a ladder has no in-between.
 *
 * The whole thing is a few thousand floats and a handful of blur passes. It is
 * rebuilt a couple of times a second for the entire match, not per bot.
 */

/** How far influence carries, in passes. Each pass reaches about a cell. */
const SPREAD_PASSES = 14;
/** What survives per cell travelled. Lower is a shorter, sharper falloff. */
const SPREAD_DECAY = 0.84;

/** A mage's weight in the presence field, before health scales it. */
const PRESENCE_WEIGHT = 1;

/**
 * What the objectives are worth before anything modifies them.
 *
 * A shrine outranks a pool because losing one ends the match, and an enemy pool
 * outranks a neutral one because taking it is worth two swings of the meter
 * rather than one.
 */
/**
 * What your own shrine is worth when nothing is happening to it.
 *
 * Very little, and that is the point. A flat, high value for home meant the
 * safest square on the map was also one of the most attractive, so a side that
 * was *winning* had bots loitering at their own shrine — there was always
 * something worth standing near and never any danger. A shrine only matters when
 * somebody is coming for it, so its worth is mostly the threat against it.
 */
const VALUE_OWN_SHRINE = 0.6;
/** How much of the danger to your shrine turns into a reason to be there. */
const VALUE_SHRINE_ALARM = 3.5;
const VALUE_ENEMY_SHRINE = 5;
const VALUE_NEUTRAL_POOL = 2.2;
const VALUE_ENEMY_POOL = 2.8;
const VALUE_OWN_POOL = 1.1;

/**
 * How much a pool is worth for being deep in enemy ground.
 *
 * The match is won by pushing toward a shrine, so the ground nearer one is worth
 * more — which turns "attack the pool that gets us closer to the enemy" from a
 * rung on a ladder into a number, and stops a Magician treating every pool as
 * equally attractive and running a shuttle between the two nearest.
 */
const VALUE_DEPTH_BONUS = 1.6;

export interface InfluenceMap {
  readonly nav: NavGrid;
  /** Where each faction's mages are, spread. Keyed by faction. */
  readonly presence: Record<Faction, Float32Array>;
  /** What ground is worth, per faction. Keyed by faction. */
  readonly value: Record<Faction, Float32Array>;
}

const FACTIONS: readonly Faction[] = ['order', 'balance', 'chaos'];

function blank(nav: NavGrid): Float32Array {
  return new Float32Array(nav.cols * nav.rows);
}

/**
 * Spreads a field outward, through open ground only.
 *
 * Walls do not conduct, which is the entire reason this is a grid rather than a
 * set of distances: three mages on the other side of a wall are not a threat to
 * where you are standing, and no falloff curve on a straight-line distance can
 * ever know that. It is why the map is worth building at all.
 */
function spread(nav: NavGrid, field: Float32Array): void {
  const { cols, rows } = nav;

  /*
   * Propagated by taking the best neighbour and discounting it, not by
   * averaging.
   *
   * Averaging is a blur, and a blur *conserves* what it spreads: one stamped
   * cell worth three, smeared over four hundred, is worth about a hundredth of a
   * point everywhere and nothing anywhere. The whole map came out flat — every
   * square within a rounding error of every other — so the tie was settled by
   * walking distance, and every bot concluded that the best place to stand was
   * exactly where it already was.
   *
   * Max-with-decay keeps the peak at the objective and falls away with distance
   * *through open ground*, which is the field everything downstream assumed it
   * was reading.
   */
  for (let pass = 0; pass < SPREAD_PASSES; pass++) {
    let changed = false;
    // Alternating sweep direction, so influence travels both ways in one pass
    // instead of only down and right.
    const back = pass % 2 === 1;
    for (let n = 0; n < rows * cols; n++) {
      const i = back ? rows * cols - 1 - n : n;
      const c = i % cols;
      const r = (i - c) / cols;
      if (!isOpen(nav, c, r)) { field[i] = 0; continue; }

      let best = field[i]!;
      for (const [dc, dr] of [[1, 0], [-1, 0], [0, 1], [0, -1]] as const) {
        const nc = c + dc;
        const nr = r + dr;
        if (nc < 0 || nr < 0 || nc >= cols || nr >= rows) continue;
        if (!isOpen(nav, nc, nr)) continue;
        const carried = field[nr * cols + nc]! * SPREAD_DECAY;
        if (carried > best) best = carried;
      }
      if (best > field[i]!) { field[i] = best; changed = true; }
    }
    if (!changed) break;
  }
}

function stamp(nav: NavGrid, field: Float32Array, at: Vec3, weight: number): void {
  const c = colOf(nav, at.x);
  const r = rowOf(nav, at.z);
  if (c < 0 || r < 0 || c >= nav.cols || r >= nav.rows) return;
  field[r * nav.cols + c] = (field[r * nav.cols + c] ?? 0) + weight;
}

/**
 * @param focus Objective the team has been told to press, if any. Its value is
 *   raised for everybody, which is the only way a stand-off ever breaks: two
 *   sides each correctly deciding the odds are even will sit there for the rest
 *   of the match, and no bot can talk itself out of that alone.
 */
export function buildInfluence(
  nav: NavGrid,
  arena: Arena,
  players: readonly PlayerState[],
  focus: Map<Faction, Vec3> = new Map(),
): InfluenceMap {
  const presence = {
    order: blank(nav), balance: blank(nav), chaos: blank(nav),
  } as Record<Faction, Float32Array>;

  for (const p of players) {
    if (!isEmbodied(p)) continue;
    // A mage on its last legs is less of a threat and less of a comfort, which
    // is what lets a wounded enemy read as an opportunity rather than a wall.
    const health = 0.35 + 0.65 * (p.hp / Math.max(1, p.hpMax));
    stamp(nav, presence[p.faction], p.pos, PRESENCE_WEIGHT * health);
  }
  for (const f of FACTIONS) spread(nav, presence[f]);

  const value = {
    order: blank(nav), balance: blank(nav), chaos: blank(nav),
  } as Record<Faction, Float32Array>;

  for (const f of FACTIONS) {
    const enemyShrines = arena.shrines.filter((s) => s.faction !== f);

    for (const shrine of arena.shrines) {
      if (shrine.faction === f) {
        if (!shrine.conquered) {
          // Quiet at home is worth almost nothing; a shrine under threat is
          // worth dropping everything for.
          const alarm = threatAt({ nav, presence, value }, f, shrine.pos);
          stamp(nav, value[f], shrine.pos, VALUE_OWN_SHRINE + alarm * VALUE_SHRINE_ALARM);
        }
      } else if (!shrine.conquered) {
        stamp(nav, value[f], shrine.pos, VALUE_ENEMY_SHRINE);
      }
    }

    for (const pool of arena.pools) {
      const base = pool.owner === f ? VALUE_OWN_POOL
        : pool.owner === 'neutral' ? VALUE_NEUTRAL_POOL
          : VALUE_ENEMY_POOL;

      // Nearer an enemy shrine is worth more: that is the direction the match
      // is won in, and it is the whole of "push toward them" as a number.
      const reach = enemyShrines.length
        ? Math.min(...enemyShrines.map(
          (s) => Math.hypot(s.pos.x - pool.pos.x, s.pos.z - pool.pos.z),
        ))
        : 60;
      const depth = VALUE_DEPTH_BONUS * Math.max(0, 1 - reach / 60);

      stamp(nav, value[f], pool.pos, base + depth);
    }

    const press = focus.get(f);
    if (press) stamp(nav, value[f], press, VALUE_ENEMY_POOL);

    spread(nav, value[f]);
  }

  return { nav, presence, value };
}

function read(nav: NavGrid, field: Float32Array, at: Vec3): number {
  const c = colOf(nav, at.x);
  const r = rowOf(nav, at.z);
  if (c < 0 || r < 0 || c >= nav.cols || r >= nav.rows) return 0;
  return field[r * nav.cols + c] ?? 0;
}

/** Everybody who is not on this side, summed. */
export function threatAt(map: InfluenceMap, faction: Faction, at: Vec3): number {
  let sum = 0;
  for (const f of FACTIONS) {
    if (f !== faction) sum += read(map.nav, map.presence[f], at);
  }
  return sum;
}

export function supportAt(map: InfluenceMap, faction: Faction, at: Vec3): number {
  return read(map.nav, map.presence[faction], at);
}

export function valueAt(map: InfluenceMap, faction: Faction, at: Vec3): number {
  return read(map.nav, map.value[faction], at);
}

/** Threat by cell index, for the sweep in `bestCell` that already has one. */
export function threatCell(map: InfluenceMap, faction: Faction, i: number): number {
  let sum = 0;
  for (const f of FACTIONS) {
    if (f !== faction) sum += map.presence[f]![i]!;
  }
  return sum;
}
